import React, { useEffect, useMemo, useState, useContext } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import {
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import EducatorLayout from './components/EducatorLayout';
import api from '../api/api';
import { AuthContext } from '../auth/AuthContext';
import SupportResourcesPanel from '../shared/SupportResourcesPanel';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const prettyMonth = (yearMonth) => {
  const [year, month] = String(yearMonth).split('-');
  const idx = Math.max(0, Math.min(11, Number(month) - 1));
  return `${MONTHS[idx] || month} ${year}`;
};

const CARD_RADIUS = 3;
const TEXT_COLOR = 'rgb(0, 21, 74)';

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-ZA', { timeZone: 'Africa/Johannesburg' }) : '-';

const formatTime = (value) =>
  value
    ? new Date(value).toLocaleTimeString('en-ZA', {
      timeZone: 'Africa/Johannesburg',
      hour: '2-digit',
      minute: '2-digit',
    })
    : '-';

const chartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <Box sx={{ p: 1.5, bgcolor: 'background.paper', color: TEXT_COLOR, borderRadius: 1, boxShadow: 3 }}>
      <Typography variant="body2" fontWeight={700}>
        {item?.payload?.month}
      </Typography>
      <Typography variant="caption">{Number(item?.value || 0).toFixed(2)} hours</Typography>
    </Box>
  );
};

const EducatorDashboard = () => {
  const { token, user } = useContext(AuthContext) ?? {};
  const [summary, setSummary] = useState({
    totalUpcomingClasses: 0,
    monthlyTeachingHours: [],
  });
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!token || !user?._id) {
        setError('You need to sign in to view the dashboard.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const { data: sessions } = await api.get(`/sessions?educatorId=${user._id}`);
        if (!isMounted) return;

        const hoursByMonth = (Array.isArray(sessions) ? sessions : []).reduce((acc, session) => {
          if (!session?.startTime || !session?.endTime) return acc;
          const start = new Date(session.startTime);
          const end = new Date(session.endTime);
          if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return acc;
          if (start.getFullYear() !== new Date().getFullYear()) return acc;
          const key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
          const hours = Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60 * 60));
          acc[key] = (acc[key] || 0) + hours;
          return acc;
        }, {});

        const currentYear = new Date().getFullYear();
        const filled = MONTHS.map((_, idx) => {
          const key = `${currentYear}-${String(idx + 1).padStart(2, '0')}`;
          return { month: prettyMonth(key), hours: hoursByMonth[key] || 0 };
        });

        const now = Date.now();
        const upcoming = (Array.isArray(sessions) ? sessions : [])
          .filter(
            (session) =>
              session?.startTime &&
              !session?.ended &&
              new Date(session.startTime).getTime() >= now
          )
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
          .slice(0, 5);

        setSummary({
          totalUpcomingClasses: upcoming.length,
          monthlyTeachingHours: filled,
        });
        setUpcomingClasses(
          upcoming.map((session) => ({
            id: session._id,
            subject: session.subject || 'Untitled session',
            date: formatDate(session.startTime),
            time: formatTime(session.startTime),
            grade: session.gradeLevel != null ? `Grade ${session.gradeLevel}` : null,
            program: session.program ? session.program.toUpperCase() : null,
          }))
        );
      } catch (err) {
        console.error('[EducatorDashboard] load failed', err);
        if (isMounted) {
          setError(err?.response?.data?.message || err?.message || 'Failed to load dashboard data.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [token, user?._id]);

  const totalHours = useMemo(
    () =>
      summary.monthlyTeachingHours.reduce((acc, item) => acc + (Number(item.hours) || 0), 0).toFixed(2),
    [summary.monthlyTeachingHours]
  );

  const StatCard = ({ title, value, icon, helper }) => (
    <Card elevation={1} sx={{ height: '100%', borderRadius: CARD_RADIUS }}>
      <CardHeader
        avatar={icon}
        title={
          <Typography variant="subtitle2" textTransform="uppercase" sx={{ color: TEXT_COLOR }}>
            {title}
          </Typography>
        }
      />
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="h4" fontWeight={800} sx={{ color: TEXT_COLOR }}>
 {loading ? <Skeleton width={80} height={42} /> : value}
          </Typography>
          {helper}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderUpcoming = () => {
    if (loading) {
      return (
        <List>
          {Array.from({ length: 3 }).map((_, idx) => (
            <ListItem key={idx} divider disableGutters>
              <ListItemText
                primary={<Skeleton width="60%" />}
                secondary={<Skeleton width="40%" />}
              />
            </ListItem>
          ))}
        </List>
      );
    }

    if (!upcomingClasses.length) {
      return (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No upcoming classes scheduled. Once you schedule sessions, they will appear here.
        </Alert>
      );
    }

    return (
      <List disablePadding>
        {upcomingClasses.map((session) => (
          <React.Fragment key={session.id}>
            <ListItem disableGutters sx={{ py: 1.5 }}>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" fontWeight={700}>
                      {session.subject}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {session.program && <Chip size="small" label={session.program} />}
                      {session.grade && <Chip size="small" label={session.grade} />}
                    </Stack>
                  </Stack>
                }
                secondary={
                  <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                    <Typography variant="body2" sx={{ color: TEXT_COLOR }}>
                      {session.date}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT_COLOR }}>
                      {session.time}
                    </Typography>
                  </Stack>
                }
              />
            </ListItem>
            <Divider component="div" />
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <EducatorLayout>
      <Box sx={{ color: TEXT_COLOR }}>
        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}

 <Card elevation={1} sx={{ borderRadius: CARD_RADIUS, mt: error ? 2 : 0 }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <StatCard
                    title="Upcoming Classes"
                    icon={<EventAvailableRoundedIcon color="primary" />}
                    value={summary.totalUpcomingClasses}
                    helper={
                      <Typography variant="body2" sx={{ color: TEXT_COLOR }}>
                        Keep preparing - your next few sessions are listed below.
                      </Typography>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StatCard
                    title="Teaching Hours This Year"
                    icon={<AccessTimeRoundedIcon color="primary" />}
                    value={`${totalHours}h`}
                    helper={
                      <Typography variant="body2" sx={{ color: TEXT_COLOR }}>
                        Sum of recorded class time across the current year.
                      </Typography>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StatCard
                    title="Latest Month"
                    icon={<TrendingUpRoundedIcon color="primary" />}
                    value={
                      <Tooltip title="Hours delivered in the latest month on record">
                        <Box component="span" sx={{ color: TEXT_COLOR }}>
                          {summary.monthlyTeachingHours[summary.monthlyTeachingHours.length - 1]?.hours || 0}h
                        </Box>
                      </Tooltip>
                    }
                    helper={
                      <Typography variant="body2" sx={{ color: TEXT_COLOR }}>
                        Peek into the chart to see monthly distribution.
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} lg={7}>
                  <Card elevation={1} sx={{ height: '100%', borderRadius: CARD_RADIUS }}>
                    <CardHeader
                      title={<Typography variant="h6" sx={{ color: TEXT_COLOR }}>Monthly Teaching Hours</Typography>}
                      subheader="Rolling distribution of recorded class time"
                      subheaderTypographyProps={{ sx: { color: TEXT_COLOR } }}
                    />
                    <CardContent sx={{ minHeight: 320 }}>
                      {loading ? (
                        <Skeleton variant="rounded" height={240} />
 ) : (
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={summary.monthlyTeachingHours}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <RechartsTooltip content={chartTooltip} />
                            <Bar dataKey="hours" radius={[6, 6, 0, 0]} fill="#1976d2" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} lg={5}>
                  <Card elevation={1} sx={{ height: '100%', borderRadius: CARD_RADIUS }}>
                    <CardHeader
                      title={<Typography variant="h6" sx={{ color: TEXT_COLOR }}>Next Sessions</Typography>}
                      subheader="A quick look at what's coming up"
                      subheaderTypographyProps={{ sx: { color: TEXT_COLOR } }}
                    />
                    <CardContent>{renderUpcoming()}</CardContent>
                  </Card>
                </Grid>
              </Grid>

              <SupportResourcesPanel userRole="educator" />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </EducatorLayout>
  );
};

export default EducatorDashboard;


